import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, MapPin, Briefcase, GraduationCap, ExternalLink, User, Award, Languages, BadgeCheck, Heart, ShieldCheck, KeyRound, Calendar } from "lucide-react";
import { getCandidateDetail, type CandidateDetail } from "@/services/candidatesApi";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CandidateAvatar } from "@/components/ui/CandidateAvatar";
import { formatLanguageProficiencyShort } from "@/lib/languageProficiency";

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("fr-FR", { month: "short", year: "numeric" }).format(new Date(iso));
}

/**
 * Formate une durée EN MOIS : années si ≥ 1 an (avec les mois restants), mois
 * sinon. Aligné sur le format canonique du front recruteur
 * (`front/src/lib/formatters.ts:formatMonthsDuration`). FR uniquement.
 *   63 → "5 ans 3 mois" · 12 → "1 an" · 11 → "11 mois" · 0/null → "—"
 */
function formatMonthsDuration(months?: number | null) {
  if (months == null || months <= 0) return "—";
  const rounded = Math.round(months);
  const years = Math.floor(rounded / 12);
  const remMonths = rounded % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years > 1 ? "ans" : "an"}`);
  if (remMonths > 0) parts.push(`${remMonths} mois`);
  return parts.join(" ") || "—";
}

const MONTHS_FR = [
  "janvier", "fevrier", "mars", "avril", "mai", "juin",
  "juillet", "aout", "septembre", "octobre", "novembre", "decembre",
];

function formatMonthYear(month?: number | null, year?: number | null): string | null {
  if (!month || !year || month < 1 || month > 12) return null;
  return `${MONTHS_FR[month - 1]} ${year}`;
}

function freshnessLabel(value?: string | null): string | null {
  if (value === "lt_1y") return "à jour (< 1 an)";
  if (value === "gt_1y") return "> 1 an";
  return null;
}

function TrustCard({ candidate }: { candidate: CandidateDetail }) {
  const memberSince = formatMonthYear(candidate.linkedin_member_since_month, candidate.linkedin_member_since_year);
  const contactFresh = freshnessLabel(candidate.linkedin_contact_info_fresh);
  const photoFresh = freshnessLabel(candidate.linkedin_photo_fresh);
  const verifications = candidate.verifications ?? [];
  const synced = candidate.about_this_profile_synced_at;

  if (!memberSince && !contactFresh && !photoFresh && verifications.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4" /> Confiance LinkedIn
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {memberSince && (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Membre depuis</span>
            <span className="font-medium">{memberSince}</span>
          </div>
        )}
        {contactFresh && (
          <div className="text-sm text-muted-foreground">
            Coordonnées <span className="text-foreground">{contactFresh}</span>
          </div>
        )}
        {photoFresh && (
          <div className="text-sm text-muted-foreground">
            Photo <span className="text-foreground">{photoFresh}</span>
          </div>
        )}
        {verifications.length > 0 && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Vérifications ({verifications.length})</p>
            <ul className="space-y-1.5">
              {verifications.map((v, i) => {
                const Icon = v.verification_type === "work_email" ? Mail : KeyRound;
                const label = v.verification_type === "work_email"
                  ? "Email professionnel"
                  : v.verification_type === "recruiter_license"
                    ? "Licence LinkedIn Recruiter"
                    : v.verification_type;
                const date = formatMonthYear(v.verified_month, v.verified_year);
                return (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Icon className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
                    <span>
                      <span className="font-medium">{v.company_name}</span>
                      <span className="text-muted-foreground"> — {label}</span>
                      {date && <span className="text-muted-foreground"> · {date}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {synced && (
          <p className="text-[11px] text-muted-foreground pt-2 border-t">
            Synchronisé le {new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(synced))}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function CandidateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState<CandidateDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getCandidateDetail(navigate, Number(id))
      .then(setCandidate)
      .catch(() => navigate("/candidates"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) return <SidebarProvider><AppSidebar /><SidebarInset><div className="p-8 text-center text-muted-foreground">Chargement...</div></SidebarInset></SidebarProvider>;
  if (!candidate) return null;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Candidat : {candidate.first_name} {candidate.last_name}</h1>
          </div>
        </header>

    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate("/candidates")}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Retour
      </Button>

      {/* Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <CandidateAvatar
              candidateId={candidate.id}
              firstName={candidate.first_name}
              lastName={candidate.last_name}
              className="h-16 w-16"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold">{candidate.first_name} {candidate.last_name}</h1>
              {candidate.title && <p className="text-muted-foreground">{candidate.title}</p>}
              {candidate.headline && candidate.headline !== candidate.title && (
                <p className="text-sm text-muted-foreground mt-1">{candidate.headline}</p>
              )}
              <div className="flex flex-wrap gap-3 mt-3 text-sm">
                {candidate.location && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-3.5 w-3.5" /> {candidate.location}
                  </span>
                )}
                {candidate.email && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" /> {candidate.email}
                  </span>
                )}
                {candidate.phone && (
                  <span className="flex items-center gap-1 text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" /> {candidate.phone}
                  </span>
                )}
                {candidate.profile_url && (
                  <a href={candidate.profile_url} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-1 text-blue-600 hover:underline">
                    <ExternalLink className="h-3.5 w-3.5" /> LinkedIn
                  </a>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metrics */}
      {(candidate.total_seniority || candidate.average_job_duration || candidate.max_gap_between_jobs) && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{formatMonthsDuration(candidate.total_seniority)}</p>
              <p className="text-xs text-muted-foreground">Ancienneté</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{formatMonthsDuration(candidate.average_job_duration)}</p>
              <p className="text-xs text-muted-foreground">Durée moy. poste</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{formatMonthsDuration(candidate.max_gap_between_jobs)}</p>
              <p className="text-xs text-muted-foreground">Plus long gap</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Summary */}
      {candidate.summary && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><User className="h-4 w-4" /> A propos</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-line">{candidate.summary}</p>
          </CardContent>
        </Card>
      )}

      {/* Confiance LinkedIn — "À propos de ce membre" (plugin v1.5+) */}
      <TrustCard candidate={candidate} />


      {/* Experiences */}
      {candidate.experiences.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Briefcase className="h-4 w-4" /> Experiences</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {candidate.experiences.map((exp) => (
              <div key={exp.id} className="border-l-2 border-muted pl-4">
                <p className="font-medium">{exp.job_title}</p>
                <p className="text-sm text-muted-foreground">
                  {exp.company_id ? (
                    <Link to={`/companies/${exp.company_id}`} className="text-blue-600 hover:underline">{exp.company_name}</Link>
                  ) : exp.company_name}
                  {exp.location && ` · ${exp.location}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(exp.start_date)} — {exp.end_date ? formatDate(exp.end_date) : "Aujourd'hui"}
                  {exp.contract_type && <Badge variant="outline" className="ml-2 text-[10px]">{exp.contract_type}</Badge>}
                </p>
                {exp.description && (
                  <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{exp.description}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Education */}
      {candidate.educations.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Formation</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {candidate.educations.map((edu) => (
              <div key={edu.id} className="border-l-2 border-muted pl-4">
                <p className="font-medium">{edu.degree || "Formation"}</p>
                <p className="text-sm text-muted-foreground">
                  {edu.school_id ? (
                    <Link to={`/schools/${edu.school_id}`} className="text-blue-600 hover:underline">{edu.school_name}</Link>
                  ) : edu.school_name}
                  {edu.field_of_study && ` · ${edu.field_of_study}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(edu.start_date)} — {edu.end_date ? formatDate(edu.end_date) : ""}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Skills */}
      {(candidate.skills?.length ?? 0) > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Award className="h-4 w-4" /> Competences ({candidate.skills!.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {candidate.skills!.map((s, i) => (
                <Badge key={i} variant="secondary">{s}</Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Languages */}
      {(candidate.languages?.length ?? 0) > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Languages className="h-4 w-4" /> Langues ({candidate.languages!.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {candidate.languages!.map((l) => (
                <Badge key={l.id} variant="secondary" className="text-xs">
                  {l.name}{l.proficiency ? ` · ${formatLanguageProficiencyShort(l.proficiency)}` : ""}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Certifications */}
      {(candidate.certifications?.length ?? 0) > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><BadgeCheck className="h-4 w-4" /> Certifications ({candidate.certifications!.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {candidate.certifications!.map((c) => (
              <div key={c.id} className="border-l-2 border-muted pl-3">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-sm">{c.name}</p>
                  {c.url && (
                    <a href={c.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {c.authority && <p className="text-xs text-muted-foreground">{c.authority}</p>}
                {(c.start_date || c.end_date) && (
                  <p className="text-[11px] text-muted-foreground">
                    {formatDate(c.start_date)} {c.end_date ? `— ${formatDate(c.end_date)}` : ""}
                  </p>
                )}
                {c.license_number && (
                  <p className="text-[11px] text-muted-foreground">N° {c.license_number}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Interests */}
      {(candidate.interests?.length ?? 0) > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Heart className="h-4 w-4" /> Centres d'interet ({candidate.interests!.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {candidate.interests!.map((i) => (
                <Badge key={i.id} variant="outline" className="text-xs">
                  {i.url ? (
                    <a href={i.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{i.name}</a>
                  ) : i.name}
                  {i.interest_type ? ` · ${i.interest_type}` : ""}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
